/* useBootstrapPages
 * Custom hook to load and set custom pages in the Redux store on application startup.
 * It fetches the pages from the API and dispatches an action to store them in the state.
 * The hook is triggered when the 'enabled' flag is true and when the current tenant ID is available.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useApiAdvancedReporting from 'system/hooks/use-api-advancedReporting';
import { customPagesActions } from 'system/store/slices/custom-page-slice';
import IRootState from 'system/types/store/IRootState';

const useBootstrapPages = (enabled: boolean) => {
  const dispatch        = useDispatch();
  const api             = useApiAdvancedReporting();
  const currentTenantId = useSelector((state: IRootState) => state.config.currentTenantId);

  useEffect(() => {
    if (!enabled || !currentTenantId) return;

    const controller = new AbortController();

    const load = async () => {
      try {
        const result = await api.widgetReport.getPages(currentTenantId, controller.signal);
        if (controller.signal.aborted) return;
        if (!result) return;
        dispatch(customPagesActions.setPages(result));
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(`[useBootstrapPages] Failed to load pages for tenant "${currentTenantId}":`, error);
      }
    };

    load();

    return () => controller.abort();
  }, [enabled, currentTenantId, api, dispatch]);
};

export default useBootstrapPages;